import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { getSubOrderPersonById } from "../../../services/SubOrderPerson";
import { getAwaitingPaymentOrdersByPerson } from "../../../services/SubOrder";
import { paymentStatusOptions } from "../SubOrder/SubOrder";
import {
  addBulkPayment,
  getBulkPaymentsByPerson,
  sendBulkPaymentApproval,
  approveBulkPayment,
  markBulkPaymentAsPaid,
  deleteBulkPayment,
  getSubOrderPersonWarehouses,
  assignSubOrderPersonWarehouses,
  removeSubOrderPersonWarehouse,
} from "../../../services/SubOrderPerson";
import { RoleBasedRender } from "../../../components/RoleBaseRender";
import WarehouseSelectingPopup from "../../../components/WarehouseSelectingPopup";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import AddIcon from "@mui/icons-material/Add";

// Import icons
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import RouteIcon from "@mui/icons-material/Route";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PaymentsIcon from "@mui/icons-material/Payments";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SendIcon from "@mui/icons-material/Send";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const InfoCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  marginBottom: theme.spacing(2),
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.03)",
  transition: "transform 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 16px 0 rgba(0,0,0,0.06)",
  },
}));

const AvatarLarge = styled(Avatar)(({ theme }) => ({
  width: theme.spacing(16),
  height: theme.spacing(16),
  fontSize: "3rem",
  backgroundColor: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
  display: "flex",
  alignItems: "center",
  "& svg": {
    fontSize: "1rem",
    marginRight: theme.spacing(0.75),
    color: theme.palette.primary.main,
  },
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 500,
  color: theme.palette.text.primary,
  marginLeft: theme.spacing(2.25),
  marginBottom: theme.spacing(1.5),
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.9rem",
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`subperson-tabpanel-${index}`}
      aria-labelledby={`subperson-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `subperson-tab-${index}`,
    "aria-controls": `subperson-tabpanel-${index}`,
  };
}

function SubOrderPersonView() {
  const theme = useTheme();
  const history = useHistory();
  const { personId } = useParams();
  const location = useLocation();

  const [subperson, setSubPerson] = useState(null);
  const [subpersonError, setSubpersonError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const initialTab = parseInt(new URLSearchParams(location.search).get("tab") || "0", 10);
  const [tabValue, setTabValue] = useState(initialTab);
  const [enableSave, setEnableSave] = useState(false);

  // Orders tab state
  const [awaitingOrders, setAwaitingOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  // Display-only sort for the bulk payment order list; defaults to latest order date first.
  // Doesn't affect the backend's oldest-first allocation order when a bulk payment is applied.
  const [orderDateSortDirection, setOrderDateSortDirection] = useState("desc");

  // Bulk payments tab state
  const [bulkPayments, setBulkPayments] = useState([]);
  const [bulkPaymentsLoading, setBulkPaymentsLoading] = useState(false);
  const [openBulkPaymentDialog, setOpenBulkPaymentDialog] = useState(false);
  const [bulkPaymentAmount, setBulkPaymentAmount] = useState("");
  const [bulkPaymentNote, setBulkPaymentNote] = useState("");
  const [bulkPaymentSubmitting, setBulkPaymentSubmitting] = useState(false);
  const [viewingPayment, setViewingPayment] = useState(null);
  const [deletePaymentError, setDeletePaymentError] = useState("");
  const [approvalExcess, setApprovalExcess] = useState(null); // { amount, paymentId }

  // Warehouse tab state
  const [warehouses, setWarehouses] = useState([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [openWarehousePopup, setOpenWarehousePopup] = useState(false);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [warehouseError, setWarehouseError] = useState("");
  const [warehouseSuccess, setWarehouseSuccess] = useState("");

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (value) => {
    const num = typeof value === "number" ? value : parseFloat(value) || 0;
    return `Rs. ${num.toFixed(2)}`;
  };

  const getInitials = (name) => `${name?.charAt(0) || ""}`;

  const formatPhoneNumber = (phoneNumberString) => {
    if (!phoneNumberString) return "N/A";
    const cleaned = ("" + phoneNumberString).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) return `(${match[1]}) ${match[2]}-${match[3]}`;
    return phoneNumberString;
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const fetchSubOrderPersonData = async () => {
    setIsLoading(true);
    try {
      let result = await getSubOrderPersonById(personId);
      if (result !== "Something went wrong...") {
        setSubPerson(result);
      } else {
        setSubpersonError("Unable to fetch sub order person data!");
      }
    } catch (error) {
      console.error("Error fetching sub order person data:", error);
      setSubpersonError("An error occurred while fetching sub order person data");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAwaitingOrders = async () => {
    setOrdersLoading(true);
    try {
      const result = await getAwaitingPaymentOrdersByPerson(personId);
      if (Array.isArray(result)) {
        setAwaitingOrders(result);
      } else {
        setAwaitingOrders([]);
      }
    } catch (error) {
      console.error("Error fetching awaiting payment orders:", error);
      setAwaitingOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchBulkPayments = async () => {
    setBulkPaymentsLoading(true);
    try {
      const result = await getBulkPaymentsByPerson(personId);
      setBulkPayments(Array.isArray(result) ? result : []);
    } catch (e) {
      setBulkPayments([]);
    } finally {
      setBulkPaymentsLoading(false);
    }
  };

  const handleSubmitBulkPayment = async () => {
    const amt = parseFloat(bulkPaymentAmount);
    if (!amt || amt <= 0) return;
    setBulkPaymentSubmitting(true);
    const result = await addBulkPayment({
      personId: parseInt(personId),
      amount: amt,
      note: bulkPaymentNote,
    });
    if (result) {
      setOpenBulkPaymentDialog(false);
      setBulkPaymentAmount("");
      setBulkPaymentNote("");
      fetchAwaitingOrders();
      fetchBulkPayments();
    }
    setBulkPaymentSubmitting(false);
  };

  const handleSendApproval = async (paymentId) => {
    await sendBulkPaymentApproval(paymentId);
    fetchBulkPayments();
  };

  const handleApprove = async (paymentId) => {
    const result = await approveBulkPayment(paymentId, true);
    fetchBulkPayments();
    fetchAwaitingOrders();
    fetchSubOrderPersonData();
    if (result?.excessAmount && result.excessAmount > 0) {
      setApprovalExcess({ amount: result.excessAmount, paymentId });
    }
  };

  const handleReject = async (paymentId) => {
    await approveBulkPayment(paymentId, false);
    fetchBulkPayments();
  };

  const handleMarkAsPaid = async (paymentId) => {
    await markBulkPaymentAsPaid(paymentId);
    fetchBulkPayments();
  };

  const handleDeletePayment = async (paymentId) => {
    setDeletePaymentError("");
    const result = await deleteBulkPayment(paymentId);
    if (result === false || result === null) {
      setDeletePaymentError("Failed to delete payment. Please try again.");
    } else {
      fetchBulkPayments();
      fetchAwaitingOrders();
    }
  };

  const fetchWarehouses = async () => {
    setWarehousesLoading(true);
    try {
      const result = await getSubOrderPersonWarehouses(personId);
      setWarehouses(Array.isArray(result) ? result : []);
    } catch (e) {
      setWarehouses([]);
    } finally {
      setWarehousesLoading(false);
    }
  };

  const handleAssignWarehouses = async () => {
    if (selectedWarehouses.length === 0) return;
    setWarehouseError("");
    setWarehouseSuccess("");
    const ids = selectedWarehouses.map((w) => w.warehouse_id);
    const result = await assignSubOrderPersonWarehouses(personId, ids);
    if (result) {
      setSelectedWarehouses([]);
      setOpenWarehousePopup(false);
      setWarehouseSuccess("Warehouses assigned successfully.");
      fetchWarehouses();
    } else {
      setWarehouseError("Failed to assign warehouses. Please try again.");
    }
  };

  const handleRemoveWarehouse = async (warehouseId) => {
    setWarehouseError("");
    setWarehouseSuccess("");
    const result = await removeSubOrderPersonWarehouse(personId, warehouseId);
    if (result !== null) {
      setWarehouseSuccess("Warehouse removed successfully.");
      fetchWarehouses();
    } else {
      setWarehouseError("Failed to remove warehouse. Please try again.");
    }
  };

  useEffect(() => {
    fetchSubOrderPersonData();
    fetchAwaitingOrders();
    fetchBulkPayments();
    fetchWarehouses();
  }, [personId]);

  const handleGoBack = () => history.goBack();

  const handleViewOrder = (orderId) => {
    history.push(`/template/sub_order_view/${orderId}`);
  };

  const totalOutstanding = awaitingOrders.reduce((sum, order) => {
    const remaining = (order.cost ?? 0) - (order.paidAmount ?? 0);
    return sum + (remaining > 0 ? remaining : 0);
  }, 0);

  const submitChanges = async () => {};

  return (
    <StyledPaper elevation={0}>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <CircularProgress />
        </Box>
      ) : subpersonError ? (
        <Box sx={{ textAlign: "center", p: 5 }}>
          <Typography variant="h6" color="error">
            {subpersonError}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleGoBack}
            startIcon={<ArrowBackIcon />}
          >
            Go Back
          </Button>
        </Box>
      ) : subperson ? (
        <>
          <HeaderCard>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <PersonIcon fontSize="large" />
              </Grid>
              <Grid item xs>
                <Typography variant="h5" fontWeight="bold">
                  Sub Order Person Details
                </Typography>
                <Typography variant="body2">
                  View and manage Sub Order Person information
                </Typography>
              </Grid>
              <Grid item>
                <Stack direction="row" spacing={2}>
                  <ActionButton
                    variant="outlined"
                    color="primary"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleGoBack}
                  >
                    Back
                  </ActionButton>
                </Stack>
              </Grid>
            </Grid>
          </HeaderCard>

          <Grid container spacing={3}>
            {/* Left column - Profile overview */}
            <Grid item xs={12} md={4}>
              <InfoCard>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <AvatarLarge>{getInitials(subperson.personName)}</AvatarLarge>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {subperson.personName}
                  </Typography>
                  <Chip
                    icon={
                      subperson.status === "Inactive" ? (
                        <LockIcon />
                      ) : (
                        <LockOpenIcon />
                      )
                    }
                    label={
                      subperson.status === "Inactive" ? "Inactive" : "Active"
                    }
                    color={
                      subperson.status === "Inactive" ? "error" : "success"
                    }
                    variant={
                      subperson.status === "Inactive" ? "outlined" : "filled"
                    }
                    sx={{ mb: 1 }}
                  />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    align="center"
                  >
                    Person ID: {subperson.personId}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mt: 2 }}>
                  <InfoLabel>
                    <EmailIcon /> Email Address
                  </InfoLabel>
                  <InfoValue>{subperson.email}</InfoValue>

                  <InfoLabel>
                    <PhoneIcon /> Phone Number
                  </InfoLabel>
                  <InfoValue>{formatPhoneNumber(subperson.phone)}</InfoValue>
                </Box>
              </InfoCard>
            </Grid>

            {/* Right column - Tabs */}
            <Grid item xs={12} md={8}>
              <InfoCard>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="subperson tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <StyledTab
                      label="Orders"
                      icon={<AssignmentOutlinedIcon />}
                      iconPosition="start"
                      {...a11yProps(0)}
                    />
                    <StyledTab
                      label="Bulk Payments"
                      icon={<PaymentsIcon />}
                      iconPosition="start"
                      {...a11yProps(1)}
                    />
                    <StyledTab
                      label="Warehouses"
                      icon={<WarehouseIcon />}
                      iconPosition="start"
                      {...a11yProps(2)}
                    />
                  </Tabs>
                </Box>

                {/* Orders Tab */}
                <TabPanel value={tabValue} index={0}>
                  <Box sx={{ p: 1 }}>
                    {approvalExcess && (
                      <Alert
                        severity="info"
                        onClose={() => setApprovalExcess(null)}
                        sx={{ mb: 2 }}
                      >
                        Payment approved with an excess of{" "}
                        <strong>{formatCurrency(approvalExcess.amount)}</strong> beyond
                        the total outstanding. This extra amount has been saved as credit
                        and will be automatically applied to this person's next order(s).
                      </Alert>
                    )}
                    {subperson.creditBalance > 0 && (
                      <Paper
                        elevation={0}
                        sx={{
                          mb: 2,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.success.main, 0.08),
                          border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                        }}
                      >
                        <AccountBalanceWalletIcon color="success" />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Available Credit Balance
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight="700"
                            color="success.main"
                          >
                            {formatCurrency(subperson.creditBalance)}
                          </Typography>
                        </Box>
                      </Paper>
                    )}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PaymentsIcon />}
                        onClick={() => setOpenBulkPaymentDialog(true)}
                        disabled={awaitingOrders.length === 0}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                      >
                        Add Bulk Payment
                      </Button>
                    </Box>
                    {ordersLoading ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={32} />
                      </Box>
                    ) : awaitingOrders.length > 0 ? (
                      <>
                        {/* Total Outstanding Banner */}
                        <Paper
                          elevation={0}
                          sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.error.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <AccountBalanceWalletIcon color="error" />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Total Outstanding Amount
                            </Typography>
                            <Typography
                              variant="h6"
                              fontWeight="700"
                              color="error.main"
                            >
                              {formatCurrency(totalOutstanding)}
                            </Typography>
                          </Box>
                          <Box sx={{ ml: "auto" }}>
                            <Chip
                              label={`${awaitingOrders.length} order${awaitingOrders.length > 1 ? "s" : ""} in progress`}
                              color="warning"
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </Paper>

                        {/* Orders Table */}
                        <TableContainer
                          component={Paper}
                          elevation={0}
                          sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}
                        >
                          <Table size="small">
                            <TableHead>
                              <TableRow
                                sx={{
                                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                                }}
                              >
                                <TableCell sx={{ fontWeight: 700 }}>Order #</TableCell>
                                <TableCell sx={{ fontWeight: 700 }} sortDirection={orderDateSortDirection}>
                                  <TableSortLabel
                                    active
                                    direction={orderDateSortDirection}
                                    onClick={() =>
                                      setOrderDateSortDirection((prev) =>
                                        prev === "desc" ? "asc" : "desc"
                                      )
                                    }
                                  >
                                    Order Date
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                  Total Cost
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                  Paid Amount
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                  Remaining
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>
                                  Action
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {[...awaitingOrders]
                                .sort((a, b) => {
                                  const diff =
                                    new Date(a.orderDate) - new Date(b.orderDate);
                                  return orderDateSortDirection === "asc" ? diff : -diff;
                                })
                                .map((order) => {
                                  const remaining =
                                    (order.cost ?? 0) - (order.paidAmount ?? 0);
                                  return (
                                  <TableRow
                                    key={order.orderId}
                                    hover
                                    sx={{
                                      "&:last-child td": { border: 0 },
                                    }}
                                  >
                                    <TableCell>
                                      <Typography
                                        variant="body2"
                                        fontWeight="600"
                                        color="primary.main"
                                      >
                                        #{order.orderId}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {formatDate(order.orderDate)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        size="small"
                                        label={
                                          paymentStatusOptions.find(
                                            (opt) => opt.value === order.paymentStatus
                                          )?.label || order.paymentStatus
                                        }
                                        color={
                                          order.paymentStatus === "PARTIALLY_PAID"
                                            ? "warning"
                                            : "default"
                                        }
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" fontWeight="500">
                                        {formatCurrency(order.cost)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography
                                        variant="body2"
                                        fontWeight="500"
                                        color="success.main"
                                      >
                                        {formatCurrency(order.paidAmount)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography
                                        variant="body2"
                                        fontWeight="700"
                                        color={remaining > 0 ? "error.main" : "success.main"}
                                      >
                                        {formatCurrency(remaining > 0 ? remaining : 0)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Tooltip title="View Order">
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<OpenInNewIcon />}
                                          onClick={() => handleViewOrder(order.orderId)}
                                          sx={{ textTransform: "none" }}
                                        >
                                          View
                                        </Button>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    ) : (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <RouteIcon
                          sx={{
                            fontSize: 48,
                            color: "text.secondary",
                            opacity: 0.5,
                            mb: 2,
                          }}
                        />
                        <Typography variant="body1" color="textSecondary">
                          No processing or awaiting payment orders for this person.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </TabPanel>

                {/* Bulk Payments Tab */}
                <TabPanel value={tabValue} index={1}>
                  <Box sx={{ p: 1 }}>
                    {deletePaymentError && (
                      <Alert
                        severity="error"
                        onClose={() => setDeletePaymentError("")}
                        sx={{ mb: 2 }}
                      >
                        {deletePaymentError}
                      </Alert>
                    )}
                    {bulkPaymentsLoading ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={32} />
                      </Box>
                    ) : bulkPayments.length > 0 ? (
                      <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Amount</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Added By</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {bulkPayments.map((payment) => {
                              const statusConfig = {
                                DRAFT: { label: "Draft", color: "default" },
                                AWAITING_APPROVAL: { label: "Awaiting Approval", color: "info" },
                                APPROVED: { label: "Approved", color: "success" },
                                REJECTED: { label: "Rejected", color: "error" },
                                PAID: { label: "Paid", color: "primary" },
                              }[payment.status] || { label: payment.status, color: "default" };
                              return (
                                <TableRow key={payment.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                                  <TableCell>
                                    <Typography variant="body2">{formatDate(payment.paymentDate)}</Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" fontWeight="600" color="primary.main">
                                      {formatCurrency(payment.totalAmount)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={statusConfig.label}
                                      color={statusConfig.color}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                      {payment.note || "—"}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">{payment.addedBy}</Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Stack direction="row" spacing={0.5} justifyContent="center">
                                      {/* View details */}
                                      <Tooltip title="View Payment Details">
                                        <IconButton
                                          size="small"
                                          onClick={() => setViewingPayment(payment)}
                                          sx={{ color: theme.palette.primary.main }}
                                        >
                                          <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>

                                      {/* Send for Approval – DRAFT only */}
                                      <RoleBasedRender allowedRoles={["SUB_ORDER_PAYMENT_CREATE", "ROLE_ADMIN"]}>
                                        {payment.status === "DRAFT" && (
                                          <Tooltip title="Send for Approval">
                                            <IconButton
                                              size="small"
                                              onClick={() => handleSendApproval(payment.id)}
                                              sx={{ color: theme.palette.info.main }}
                                            >
                                              <SendIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </RoleBasedRender>

                                      {/* Approve – AWAITING_APPROVAL only */}
                                      <RoleBasedRender allowedRoles={["SUB_ORDER_PAYMENT_APPROVE", "ROLE_ADMIN"]}>
                                        {payment.status === "AWAITING_APPROVAL" && (
                                          <Tooltip title="Approve">
                                            <IconButton
                                              size="small"
                                              onClick={() => handleApprove(payment.id)}
                                              sx={{ color: theme.palette.success.main }}
                                            >
                                              <CheckIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </RoleBasedRender>

                                      {/* Reject – AWAITING_APPROVAL only */}
                                      <RoleBasedRender allowedRoles={["SUB_ORDER_PAYMENT_APPROVE", "ROLE_ADMIN"]}>
                                        {payment.status === "AWAITING_APPROVAL" && (
                                          <Tooltip title="Reject">
                                            <IconButton
                                              size="small"
                                              onClick={() => handleReject(payment.id)}
                                              sx={{ color: theme.palette.error.main }}
                                            >
                                              <CancelIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </RoleBasedRender>

                                      {/* Mark as Paid – APPROVED only */}
                                      <RoleBasedRender allowedRoles={["SUB_ORDER_PAYMENT_CREATE", "ROLE_ADMIN"]}>
                                        {payment.status === "APPROVED" && (
                                          <Tooltip title="Mark as Paid">
                                            <IconButton
                                              size="small"
                                              onClick={() => handleMarkAsPaid(payment.id)}
                                              sx={{ color: theme.palette.success.main }}
                                            >
                                              <AccountBalanceWalletIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </RoleBasedRender>

                                      {/* Delete – DRAFT or REJECTED only */}
                                      <RoleBasedRender allowedRoles={["SUB_ORDER_PAYMENT_DELETE", "ROLE_ADMIN"]}>
                                        {(payment.status === "DRAFT" || payment.status === "REJECTED") && (
                                          <Tooltip title="Delete">
                                            <IconButton
                                              size="small"
                                              onClick={() => handleDeletePayment(payment.id)}
                                              sx={{ color: theme.palette.error.main }}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </RoleBasedRender>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <PaymentsIcon sx={{ fontSize: 48, color: "text.secondary", opacity: 0.5, mb: 2 }} />
                        <Typography variant="body1" color="textSecondary">
                          No bulk payments recorded for this person.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </TabPanel>
                {/* Warehouses Tab */}
                <TabPanel value={tabValue} index={2}>
                  <Box sx={{ p: 1 }}>
                    {warehouseError && (
                      <Alert severity="error" onClose={() => setWarehouseError("")} sx={{ mb: 2 }}>
                        {warehouseError}
                      </Alert>
                    )}
                    {warehouseSuccess && (
                      <Alert severity="success" onClose={() => setWarehouseSuccess("")} sx={{ mb: 2 }}>
                        {warehouseSuccess}
                      </Alert>
                    )}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                      <RoleBasedRender allowedRoles={["SUB_ORDER_PERSON_UPDATE", "ROLE_ADMIN"]}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={() => setOpenWarehousePopup(true)}
                          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                        >
                          Assign Warehouses
                        </Button>
                      </RoleBasedRender>
                    </Box>
                    {warehousesLoading ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={32} />
                      </Box>
                    ) : warehouses.length > 0 ? (
                      <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                              <TableCell sx={{ fontWeight: 700 }}>Warehouse Name</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700 }}>Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {warehouses.map((warehouse) => (
                              <TableRow key={warehouse.warehouseId} hover sx={{ "&:last-child td": { border: 0 } }}>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="600">
                                    {warehouse.warehouseName}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {warehouse.warehouseType}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={warehouse.isActive ? "Active" : "Inactive"}
                                    color={warehouse.isActive ? "success" : "default"}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <RoleBasedRender allowedRoles={["SUB_ORDER_PERSON_UPDATE", "ROLE_ADMIN"]}>
                                    <Tooltip title="Remove Warehouse">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleRemoveWarehouse(warehouse.warehouseId)}
                                        sx={{ color: theme.palette.error.main }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </RoleBasedRender>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <WarehouseIcon sx={{ fontSize: 48, color: "text.secondary", opacity: 0.5, mb: 2 }} />
                        <Typography variant="body1" color="textSecondary">
                          No warehouses assigned to this person.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </TabPanel>
              </InfoCard>

              <ActionButton
                disabled={!enableSave}
                variant="outlined"
                color="primary"
                onClick={submitChanges}
              >
                Save
              </ActionButton>
            </Grid>
          </Grid>
        </>
      ) : (
        <Box sx={{ textAlign: "center", p: 5 }}>
          <Typography variant="h6" color="textSecondary">
            No sub order person data found
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleGoBack}
            startIcon={<ArrowBackIcon />}
          >
            Go Back
          </Button>
        </Box>
      )}
      {/* Add Bulk Payment Dialog */}
      <Dialog
        open={openBulkPaymentDialog}
        onClose={() => setOpenBulkPaymentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Add Bulk Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The payment will be applied to the oldest orders first. Any excess amount will flow to the next order.
          </Typography>
          <TextField
            label="Payment Amount"
            type="number"
            inputProps={{ min: 0, step: "0.01" }}
            value={bulkPaymentAmount}
            onChange={(e) => setBulkPaymentAmount(e.target.value)}
            onWheel={(e) => e.target.blur()}
            fullWidth
            required
            sx={{ mb: 2 }}
          />
          <TextField
            label="Note (optional)"
            value={bulkPaymentNote}
            onChange={(e) => setBulkPaymentNote(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenBulkPaymentDialog(false)}
            disabled={bulkPaymentSubmitting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitBulkPayment}
            disabled={bulkPaymentSubmitting || !bulkPaymentAmount || parseFloat(bulkPaymentAmount) <= 0}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {bulkPaymentSubmitting ? <CircularProgress size={20} /> : "Submit Payment"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Warehouse Selection Popup */}
      {openWarehousePopup && (
        <>
          <WarehouseSelectingPopup
            setOpenWarehouseTable={setOpenWarehousePopup}
            openWarehouseTable={openWarehousePopup}
            handleCloseWarehouseTable={() => {
              setOpenWarehousePopup(false);
              setSelectedWarehouses([]);
            }}
            setSelectedWarehouse={setSelectedWarehouses}
            selectedWarehouse={selectedWarehouses}
            skipList={warehouses.map((w) => w.warehouseId)}
            isOneChoise={false}
          />
          {selectedWarehouses.length > 0 && (
            <Box
              sx={{
                position: "fixed",
                bottom: 24,
                right: 24,
                zIndex: 1400,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleAssignWarehouses}
                sx={{ fontWeight: 700, borderRadius: 2, boxShadow: 4 }}
              >
                Assign {selectedWarehouses.length} Warehouse{selectedWarehouses.length > 1 ? "s" : ""}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* View Bulk Payment Detail Dialog */}
      <Dialog
        open={Boolean(viewingPayment)}
        onClose={() => setViewingPayment(null)}
        maxWidth="sm"
        fullWidth
      >
        {viewingPayment && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>
              Bulk Payment Details
            </DialogTitle>
            <DialogContent>
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Date: <strong>{formatDate(viewingPayment.paymentDate)}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Amount: <strong>{formatCurrency(viewingPayment.totalAmount)}</strong>
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Chip
                    label={viewingPayment.status}
                    size="small"
                    color={
                      viewingPayment.status === "PAID" ? "primary"
                        : viewingPayment.status === "APPROVED" ? "success"
                        : viewingPayment.status === "REJECTED" ? "error"
                        : viewingPayment.status === "AWAITING_APPROVAL" ? "info"
                        : "default"
                    }
                    variant="outlined"
                  />
                </Box>
                {viewingPayment.note && (
                  <Typography variant="body2" color="text.secondary">
                    Note: <strong>{viewingPayment.note}</strong>
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Added By: <strong>{viewingPayment.addedBy}</strong>
                </Typography>
                {viewingPayment.approvedBy && (
                  <Typography variant="body2" color="text.secondary">
                    {viewingPayment.status === "REJECTED" ? "Rejected By" : "Approved By"}:{" "}
                    <strong>{viewingPayment.approvedBy}</strong>
                  </Typography>
                )}
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Applied to Orders
              </Typography>
              {viewingPayment.allocations && viewingPayment.allocations.length > 0 ? (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                        <TableCell sx={{ fontWeight: 700 }}>Order #</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Order Date</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Applied Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {viewingPayment.allocations.map((alloc) => (
                        <TableRow key={alloc.id} sx={{ "&:last-child td": { border: 0 } }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="600" color="primary.main">
                              #{alloc.subOrder?.orderId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(alloc.subOrder?.orderDate)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="700" color="success.main">
                              {formatCurrency(alloc.allocatedAmount)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No allocations found for this payment.
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setViewingPayment(null)} sx={{ textTransform: "none" }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </StyledPaper>
  );
}

export default SubOrderPersonView;
